import { Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@services';

interface AnswerRow {
    label?: string;
    isCorrectOption?: boolean;
    isSelected?: boolean;
    correctText?: string;
    studentText?: string;
    isChoiceCorrect?: boolean;
}

/**
 * The backend already computes per-choice correctness after grading (e.g.
 * "correctly left unselected" counts as right for progressive-mode multi-select
 * questions) — always prefer that over re-deriving it here, since a naive
 * selected-and-correct check can't represent "correctly did NOT select this".
 */
function resolveIsCorrect(entry: any, fallback: boolean): boolean {
    if (!entry) { return fallback; }
    if (typeof entry.isCorrect === 'boolean') { return entry.isCorrect; }
    if (typeof entry.is_correct === 'boolean') { return entry.is_correct; }
    return fallback;
}

interface QuestionReview {
    id: number;
    title: string;
    display: 'binary' | 'rows' | 'text' | 'unanswered';
    correctLabel?: string;
    studentLabel?: string;
    rows?: Array<AnswerRow>;
    studentText?: string;
    isCorrect?: boolean;
    ratioPercent?: number;
}

@Component({
    selector: 'msc-manage-instance-learner-exercise-answer-review',
    templateUrl: './exercise-answer-review.component.html',
    styleUrls: ['./exercise-answer-review.component.scss']
})
export class ManageInstanceLearnerExerciseAnswerReviewComponent implements OnChanges {
    @Input() questions: Array<any>;
    public reviews: Array<QuestionReview> = [];

    constructor(
        private readonly translateService: TranslateService,
    ) { }

    ngOnChanges() {
        this.reviews = (this.questions || []).map((question) => this.buildReview(question));
    }

    /**
     *
     */
    private buildReview(question: any): QuestionReview {
        const base = {
            id: question.id,
            title: question.title,
        };

        if (!question.session) {
            return { ...base, display: 'unanswered' as const };
        }

        const isCorrect = question.session.isCorrect;
        // ratio is a 0-1 fraction on the wire (question_sessions.ratio, unmultiplied) — surfacing it
        // as an explicit percentage instead of just a correct/incorrect icon, since partial-credit
        // question types (e.g. "select all that apply") can be marked incorrect overall while still
        // scoring above 0%, which a single check/x icon can't represent on its own.
        const ratioPercent = typeof question.session.ratio === 'number' ? Math.round(question.session.ratio * 100) : null;

        switch (question.type) {
            case 'true-false':
                return {
                    ...base,
                    display: 'binary',
                    correctLabel: this.translateService.instant(question.param?.choice ? 'words.yes' : 'words.no'),
                    studentLabel: this.translateService.instant(question.session.answer?.choice ? 'words.yes' : 'words.no'),
                    isCorrect,
                    ratioPercent,
                };

            case 'multiple':
            case 'ucq':
                return { ...base, display: 'rows', rows: this.buildChoiceRows(question), isCorrect, ratioPercent };

            case 'fill-in':
                return { ...base, display: 'rows', rows: this.buildFillInRows(question), isCorrect, ratioPercent };

            case 'ordered':
            case 'procedure':
                return { ...base, display: 'rows', rows: this.buildOrderRows(question), isCorrect, ratioPercent };

            case 'linker':
                return { ...base, display: 'rows', rows: this.buildLinkerRows(question), isCorrect, ratioPercent };

            case 'category':
                return { ...base, display: 'rows', rows: this.buildCategoryRows(question), isCorrect, ratioPercent };

            case 'open-write':
            default:
                return {
                    ...base,
                    display: 'text',
                    studentText: question.session.answer?.text,
                    isCorrect,
                    ratioPercent,
                };
        }
    }

    /**
     *
     */
    private buildChoiceRows(question: any): Array<AnswerRow> {
        const correctChoices = question.content?.choices || [];
        const studentChoices = question.session.answer?.choices || [];

        return correctChoices.map((choice: any, index: number) => {
            const studentChoice = studentChoices[index];
            const isSelected = !!studentChoice?.state;
            const isCorrectOption = !!choice.state;

            return {
                label: choice.choice,
                isCorrectOption,
                isSelected,
                isChoiceCorrect: resolveIsCorrect(studentChoice, isSelected === isCorrectOption),
            };
        });
    }

    /**
     *
     */
    private buildFillInRows(question: any): Array<AnswerRow> {
        const correctChoices = question.content?.choices || [];
        const studentChoices = question.session.answer?.choices || [];

        return correctChoices.map((choice: any, index: number) => {
            const correctText = choice.choice;
            const studentChoice = studentChoices[index];
            const studentText = studentChoice?.choice ?? '';
            const fallback = this.normalize(correctText) === this.normalize(studentText);

            return { correctText, studentText, isChoiceCorrect: resolveIsCorrect(studentChoice, fallback) };
        });
    }

    /**
     *
     */
    private buildOrderRows(question: any): Array<AnswerRow> {
        const correctChoices = question.content?.choices || [];
        const studentChoices = question.session.answer?.choices || [];

        return correctChoices.map((choice: any, index: number) => {
            const correctText = choice.choice;
            const studentChoice = studentChoices[index];
            const studentChoiceValue = studentChoice?.choice;
            const studentText = Array.isArray(studentChoiceValue) ? studentChoiceValue.join(' ') : (studentChoiceValue ?? '');
            const fallback = this.normalize(correctText) === this.normalize(studentText);

            return { correctText, studentText, isChoiceCorrect: resolveIsCorrect(studentChoice, fallback) };
        });
    }

    /**
     *
     */
    private buildLinkerRows(question: any): Array<AnswerRow> {
        const links = question.content?.links || [];
        const correctChoices = question.content?.choices || [];
        const studentChoices = question.session.answer?.choices || [];

        return links.map((link: string, index: number) => {
            const studentChoice = studentChoices[index];
            const fallback = this.normalize(correctChoices[index]) === this.normalize(studentChoice?.name);

            return {
                label: link,
                correctText: correctChoices[index],
                studentText: studentChoice?.name ?? '',
                isChoiceCorrect: resolveIsCorrect(studentChoice, fallback),
            };
        });
    }

    /**
     *
     */
    private buildCategoryRows(question: any): Array<AnswerRow> {
        const correctChoices = question.content?.choices || [];
        const studentChoices = question.session.answer?.choices || [];

        return correctChoices.map((choice: any, index: number) => {
            const studentChoice = studentChoices[index];
            const fallback = this.normalize(choice.category) === this.normalize(studentChoice?.category);

            return {
                label: choice.value,
                correctText: choice.category,
                studentText: studentChoice?.category ?? '',
                isChoiceCorrect: resolveIsCorrect(studentChoice, fallback),
            };
        });
    }

    /**
     *
     */
    private normalize(value: any): string {
        return (value ?? '').toString().trim().toLowerCase();
    }
}
